import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Mic,
  Calendar,
  Clock,
  User,
  BookOpen,
  Award,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Search,
  TrendingUp,
  MessageSquare,
  Code2,
  Video,
  MapPin,
  RefreshCw,
  Eye,
  Layers,
  ChevronRight,
  Sparkles,
  Info,
  X
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { getStudentMockInterviews } from '../../../../services/MockInterview';
import { getStudentOptions } from '../../../../services/StudentService';
import NewCustomAsyncSelectDropdown from '../../common/NewCustomAsyncSelectDropdown';
import { silenceFor } from '../../common/Loader/busy';
import debounce from 'lodash/debounce';
import moment from 'moment';
import NewCustomDataTable from '../../common/NewCustomDataTable.jsx';
import useScrollLoadMore from "../../common/useScrollLoadMore";
import '../../Trainer/TrainerDashboard.css';

const StudentMockInterview = () => {
  const { user } = useAuth();
  const userData = user?.user || user;

  const isAdminOrTrainer = useMemo(() => {
    if (!userData) return false;
    if (userData.isSuperAdmin === true || userData.isSuperAdmin === 'true' || userData.isSuperAdmin === 1) return true;
    return userData.profiles?.some((p) => {
      const label = String(p.label || p.name || '').toLowerCase();
      return ['admin', 'superadmin', 'trainer', 'crm'].includes(label);
    });
  }, [userData]);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [studentInfo, setStudentInfo] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'qualified', 'not-qualified', 'pending'
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Modal State
  const [selectedInterview, setSelectedInterview] = useState(null);

  /* Infinite scroll, the shape ListAttendance uses: no pager buttons,
     an observer on the foot of the list as the page scrolls, and a count
     under the list. getStudentMockInterviews returns every row at once,
     so this reveals more of what is already in hand. */
  const SCROLL_STEP = 20;
  const [shownCount, setShownCount] = useState(SCROLL_STEP);
  const listRef = useRef(null);
  // The foot under the list -- the sentinel, in the flow of the page.
  const listEndRef = useRef(null);

  const [showModal, setShowModal] = useState(false);

  // Admin student dropdown state
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const isFetchingStudents = useRef(false);

  // Fetch admin students list for dropdown
  const fetchStudents = useCallback(async (search = '', pageNum = 1) => {
    try {
      isFetchingStudents.current = true;
      // Names only (server skips the join): the picker reads id, name, email.
      const response = await getStudentOptions(20, pageNum, search);
      const studentsList = response.data || [];
      const hasMore = studentsList.length === 20;
      return { students: studentsList, hasMore };
    } catch (err) {
      console.error('Error fetching students:', err);
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudents.current = false;
    }
  }, []);

  const loadStudents = useCallback(
    async (search = '', pageNum = 1, append = false) => {
      setLoadingStudents(true);
      const { students: studentsList, hasMore } = await fetchStudents(search, pageNum);

      const newOptions = studentsList.map((s) => ({
        value: s.id,
        label: `${s.name} (${s.email})`,
        student: s,
      }));

      if (append) {
        setStudentOptions((prev) => [...prev, ...newOptions]);
      } else {
        setStudentOptions(newOptions);
      }

      setHasMoreStudents(hasMore);
      setLoadingStudents(false);
      return { students: studentsList, hasMore };
    },
    [fetchStudents]
  );

  const handleStudentSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setStudentSearchTerm(inputValue);
        setStudentPage(1);
        await loadStudents(inputValue, 1, false);
      }, 700),
    [loadStudents]
  );

  const handleLoadMoreStudents = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetchingStudents.current) return;
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [hasMoreStudents, loadingStudents, studentPage, studentSearchTerm, loadStudents]);

  const handleDropdownOpen = useCallback(async () => {
    if (isAdminOrTrainer && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents('', 1, false);
    }
  }, [isAdminOrTrainer, studentOptions.length, loadingStudents, loadStudents]);

  const handleRefreshStudents = useCallback(async () => {
    setStudentPage(1);
    setStudentSearchTerm('');
    await loadStudents('', 1, false);
  }, [loadStudents]);

  /* One dropdown, rendered into the gate before a student is picked and
     into the strip afterwards. Declared below every handler and list it
     reads, so nothing is referenced before it is initialised. */
  const studentPicker = (
    <NewCustomAsyncSelectDropdown
      options={studentOptions}
      value={selectedStudent ? [selectedStudent] : []}
      onChange={(opt) => {
        const chosen = Array.isArray(opt) ? opt[0] || null : opt;
        setSelectedStudent(chosen);
      }}
      placeholder="Search by name, email or phone…"
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMoreStudents}
      hasMore={hasMoreStudents}
      isClearable
      onMenuOpen={handleDropdownOpen}
      onRefresh={handleRefreshStudents}
    />
  );

  // Fetch student mock interviews (fetches complete list for selected student)
  const loadMockInterviews = useCallback(async () => {
    try {
      // The list draws skeleton rows itself (NewCustomDataTable), so the
      // global overlay stays down for this load.
      silenceFor();
      setLoading(true);
      setError(null);

      const params = {};

      if (isAdminOrTrainer) {
        if (selectedStudent?.value) {
          params.studentId = selectedStudent.value;
        } else {
          setInterviews([]);
          setStudentInfo(null);
          setLoading(false);
          return;
        }
      } else {
        params.userId = userData?.id;
        params.email = userData?.email;
      }

      const res = await getStudentMockInterviews(params);
      setInterviews(res.data || []);
      setStudentInfo(res.student || null);
    } catch (err) {
      console.error('Error loading mock interviews:', err);
      setError(err?.response?.data?.message || 'Failed to load mock interviews. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedStudent, isAdminOrTrainer, userData]);

  useEffect(() => {
    loadMockInterviews();
  }, [loadMockInterviews]);

  // Dynamically calculate accurate counts across all 4 categories from full interviews list
  const counts = useMemo(() => {
    const total = interviews.length;
    let qualified = 0;
    let notQualified = 0;
    let pending = 0;

    for (const item of interviews) {
      const statusLower = String(item.status || '').toLowerCase();
      const resultLower = String(item.result || '').toLowerCase();

      if (
        statusLower === 'not qualified' ||
        statusLower === 'unqualified' ||
        resultLower.includes('unqualif') ||
        resultLower.includes('not qualified') ||
        resultLower === 'fail'
      ) {
        notQualified++;
      } else if (
        statusLower === 'qualified' ||
        (resultLower.includes('qualif') && !resultLower.includes('unqualif') && !resultLower.includes('not')) ||
        resultLower === 'pass'
      ) {
        qualified++;
      } else {
        pending++;
      }
    }

    return { total, qualified, notQualified, pending };
  }, [interviews]);

  // Filtered interviews according to search and selected status tab
  const filteredInterviews = useMemo(() => {
    return interviews.filter((item) => {
      // Search match
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matches = (
          String(item.name || '').toLowerCase().includes(q) ||
          String(item.batch?.name || '').toLowerCase().includes(q) ||
          String(item.batch?.course?.name || '').toLowerCase().includes(q) ||
          String(item.trainer?.name || '').toLowerCase().includes(q) ||
          String(item.status || '').toLowerCase().includes(q) ||
          String(item.feedback || '').toLowerCase().includes(q)
        );
        if (!matches) return false;
      }

      // Status tab match
      const statusLower = String(item.status || '').toLowerCase();
      const resultLower = String(item.result || '').toLowerCase();

      if (statusFilter === 'qualified') {
        return (
          statusLower === 'qualified' ||
          (resultLower.includes('qualif') && !resultLower.includes('unqualif') && !resultLower.includes('not')) ||
          resultLower === 'pass'
        );
      }
      if (statusFilter === 'not-qualified') {
        return (
          statusLower === 'not qualified' ||
          statusLower === 'unqualified' ||
          resultLower.includes('unqualif') ||
          resultLower.includes('not qualified') ||
          resultLower === 'fail'
        );
      }
      if (statusFilter === 'pending') {
        return (
          !item.isEvaluated ||
          statusLower === 'pending' ||
          statusLower === 'scheduled' ||
          statusLower === 'completed'
        );
      }
      return true; // 'all'
    });
  }, [interviews, searchTerm, statusFilter]);

  /* These two read filteredInterviews, so they must sit BELOW it — it is
     a const from useMemo, and a hook placed above it dies in the temporal
     dead zone with "Cannot access 'filteredInterviews' before
     initialization". Neither eslint nor the bundler catches that. */
  useEffect(() => {
    setShownCount(SCROLL_STEP);
  }, [filteredInterviews]);

  // The page is the scroller: the foot of the list reaching the viewport
  // is what asks for the next slice. See useScrollLoadMore.
  useScrollLoadMore(
    listEndRef,
    () => setShownCount((n) => (n >= filteredInterviews.length ? n : n + SCROLL_STEP)),
    [filteredInterviews, shownCount],
  );

  const handleViewDetails = (interview) => {
    setSelectedInterview(interview);
    setShowModal(true);
  };

  /* Status as one of the shared pills, rather than the hand-rolled
     badge this page used to build. */
  const statusPill = (status, result) => {
    const s = String(status || '').toLowerCase();
    const r = String(result || '').toLowerCase();
    if (
      s === 'not qualified' || s === 'unqualified' ||
      r.includes('unqualif') || r.includes('not qualified') || r === 'fail'
    ) {
      return { label: 'Not qualified', cls: 'pg-pill pg-pill--bad' };
    }
    if (s === 'qualified' || r.includes('qualif') || r === 'pass') {
      return { label: 'Qualified', cls: 'pg-pill pg-pill--good' };
    }
    if (s === 'completed') return { label: 'Completed', cls: 'pg-pill pg-pill--good' };
    return { label: status || 'Scheduled', cls: 'pg-pill pg-pill--live' };
  };

  const venueOf = (iv) =>
    iv.onlineLink
      ? 'Online live session'
      : iv.offlineAddress || iv.classroomNumber || 'Offline classroom';

  /* Read as UTC, as every staff screen reads them: the event form stores the
     day as that date's UTC midnight and the time as the clock time the
     trainer typed, saved as UTC on 1970-01-01. Read in local time, a 10:00
     interview showed here as 3:30 PM in IST. */
  const whenOf = (iv) => ({
    day: iv.date ? moment.utc(iv.date).format('DD MMM YYYY') : 'Date TBA',
    time: iv.time ? moment.utc(iv.time).format('hh:mm A') : 'Time TBA',
  });

  /* Five columns, and only five. Batch, trainer, venue, the mark
     breakdown and the trainer's feedback are all in the popup, where
     there is room for them and where they are actually read. */
  const headers = [
    {
      title: 'Interview',
      prop: 'name',
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-strong">{row.name || '—'}</span>,
    },
    {
      title: 'Course',
      prop: 'courseName',
      searchable: true,
      sortable: true,
      cell: (row) => (
        <span className="pg-course">{row.batch?.course?.name || '—'}</span>
      ),
    },
    {
      title: 'When',
      prop: 'date',
      sortable: true,
      cell: (row) => {
        const w = whenOf(row);
        return (
          <span className="pg-date">
            {w.day} · {w.time}
          </span>
        );
      },
    },
    {
      title: 'Status',
      prop: 'status',
      sortable: true,
      cell: (row) => {
        const p = statusPill(row.status, row.result);
        return <span className={p.cls}>{p.label}</span>;
      },
    },
    {
      title: 'Score',
      prop: 'mark',
      sortable: true,
      cell: (row) =>
        row.isEvaluated ? (
          <span className="pg-strong">
            {row.mark != null ? row.mark : 0}/{row.totalMark || 20}
          </span>
        ) : (
          <span className="pg-none">—</span>
        ),
    },
  ];

  /* ── The record popup ──────────────────────────────────────────
     Built to the same plan as the attendance list's popup: title and
     a pgm-ident row of labelled facts in the header, pgm-section
     blocks in the body each led by a pgm-section-title, pgm-table for
     anything with more than one value, pgm-empty when there is
     nothing, and a footer whose actions sit left of a filled Close.
     ---------------------------------------------------------------- */
  const detail = selectedInterview
    ? (() => {
        const iv = selectedInterview;
        const p = statusPill(iv.status, iv.result);
        const w = whenOf(iv);
        const qualified = p.cls.includes('good');

        const marks = [
          { label: 'Technical', got: iv.technicalMark, outOf: 10 },
          { label: 'Communication', got: iv.communicationMark, outOf: 10 },
          { label: 'Overall', got: iv.mark, outOf: iv.totalMark || 20 },
        ];

        return (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="smi-detail-title"
            onClick={() => setSelectedInterview(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              onClick={(e) => e.stopPropagation()}
            >
              {/* The interview is what the record is about, so it is the
                  title — in full, never clipped the way the list clips it. */}
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <h2 className="pgm-batch" id="smi-detail-title">
                    {iv.name || 'Mock interview'}
                  </h2>
                  <dl className="pgm-ident">
                    <div className="pgm-ident-row">
                      <dt>Trainer</dt>
                      <dd>{iv.trainer?.name || 'Assigned trainer'}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Course</dt>
                      <dd>{iv.batch?.course?.name || '—'}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Date</dt>
                      <dd>{w.day}</dd>
                    </div>
                  </dl>
                </div>

                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setSelectedInterview(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                {/* Marks first: how it went, before where and when it was. */}
                <section className="pgm-section">
                  <h3 className="pgm-section-title">
                    Evaluation
                    <span className="pgm-section-count">
                      {iv.isEvaluated ? `${iv.mark ?? 0}/${iv.totalMark || 20}` : '—'}
                    </span>
                  </h3>

                  {!iv.isEvaluated ? (
                    <p className="pgm-empty">
                      Marks and feedback are added after the interview has taken place.
                    </p>
                  ) : (
                    <table className="pgm-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Section</th>
                          <th>Marks</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marks.map((m, i) => (
                          <tr key={m.label}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{m.label}</td>
                            <td>
                              {m.got != null ? `${m.got}/${m.outOf}` : '—'}
                            </td>
                            <td>
                              {m.label === 'Overall' ? (
                                <span
                                  className={`pgm-pill ${
                                    qualified ? 'pgm-pill--good' : 'pgm-pill--bad'
                                  }`}
                                >
                                  {iv.percentage != null ? `${iv.percentage}%` : p.label}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>

                <section className="pgm-section">
                  <h3 className="pgm-section-title">Schedule</h3>
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pgm-num">1</td>
                        <td>Status</td>
                        <td>
                          <span className={p.cls}>{p.label}</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="pgm-num">2</td>
                        <td>Time</td>
                        <td>{w.time}</td>
                      </tr>
                      <tr>
                        <td className="pgm-num">3</td>
                        <td>Batch</td>
                        <td>{iv.batch?.name || '—'}</td>
                      </tr>
                      <tr>
                        <td className="pgm-num">4</td>
                        <td>Where</td>
                        <td>{venueOf(iv)}</td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                <section className="pgm-section">
                  <h3 className="pgm-section-title">Trainer feedback</h3>
                  {iv.feedback ? (
                    <p className="pgm-sub">{iv.feedback}</p>
                  ) : (
                    <p className="pgm-empty">No feedback has been recorded yet.</p>
                  )}
                </section>
              </div>

              {/* What you can do with the record sits with Close, at the
                  end — you read the record, then act on it. Close stays
                  last and is the only filled button. */}
              <footer className="pg-modal-foot">
                <div className="pgm-actions">
                  {iv.onlineLink && (
                    <a
                      className="pgm-btn"
                      href={
                        iv.onlineLink.startsWith('http')
                          ? iv.onlineLink
                          : `https://${iv.onlineLink}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join session
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setSelectedInterview(null)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        );
      })()
    : null;

  return (
    <div className="dashboard-wrapper">
      <div className="td-page">
        {/* Staff pick whose interviews they are reading. Before anything
            is chosen the picker sits inside the gate -- the same card
            /student-profile and /student-quizzes show -- because the page
            has nothing else to display yet. Afterwards it becomes the
            compact strip. One control, two placements. */}
        {isAdminOrTrainer && !selectedStudent && (
          <div className="pg-gate">
            <span className="pg-gate-mark">
              <User size={22} aria-hidden="true" />
            </span>
            <h1 className="pg-gate-title">Pick a student</h1>
            <p className="pg-gate-text">
              Choose a student to see the mock interviews scheduled for them.
            </p>

            <div className="pg-gate-field">{studentPicker}</div>

            <span className="pg-gate-tip">
              <AlertCircle size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        )}

        {/* Not a filter panel -- a readout of WHOSE interviews are on
            screen, with the control that swaps them. Behind a Filters
            button on a phone it said nothing, and whose list this is was
            the one thing a staff member needed it to say. It is one
            picker and a Clear; the sheet is for the six- and
            seven-control strips the list pages carry. */}
        {isAdminOrTrainer && selectedStudent && (
          <div className="pg-filters">
            <div className="pg-filters-row">
              <div className="pg-field pg-field--grow">
                <label className="pg-label">Viewing student</label>
                {studentPicker}
              </div>

              <button
                type="button"
                className="pg-btn"
                onClick={() => setSelectedStudent(null)}
              >
                <X size={13} aria-hidden="true" />
                Clear
              </button>
            </div>
          </div>
        )}

        {error ? (
          <div className="pg-state">
            <span className="pg-state-icon">
              <AlertCircle size={26} aria-hidden="true" />
            </span>
            <h3 className="pg-state-title">Unable to load mock interviews</h3>
            <p className="pg-state-text">{error}</p>
          </div>
        ) : isAdminOrTrainer && !selectedStudent ? null : (
          <div className="td-list td-list--mock" ref={listRef}>
            <NewCustomDataTable
              data={filteredInterviews.slice(0, shownCount)}
              headers={headers}
              loading={loading}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setSelectedInterview(row)}
              /* Every cell wraps its text in a span, so the default
                 ['TR','TD'] would leave the text itself unclickable. */
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: 'button',
                'aria-label': `Open ${row.name || 'this mock interview'}`,
                onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedInterview(row);
                  }
                },
              })}
            />

            {/* What the page buttons used to say, said once. */}
            <div className="pg-scroll-foot" ref={listEndRef}>
              {shownCount < filteredInterviews.length ? (
                <span className="pg-scroll-count">
                  {shownCount} of {filteredInterviews.length}
                </span>
              ) : (
                filteredInterviews.length > 0 && (
                  <span className="pg-scroll-count">
                    All {filteredInterviews.length} interview
                    {filteredInterviews.length === 1 ? '' : 's'} loaded
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {detail}
    </div>
  );
};

export default StudentMockInterview;
